import { ArrowLeft } from "lucide-react";

// Shape definition with inline SVG data
export interface ShapeDefinition {
  id: string;
  name: string;
  category: string;
  // SVG viewBox dimensions
  viewBox: string;
  // SVG path/shape content (without the <svg> wrapper)
  content: string;
}

// Predefined shapes library
export const SHAPE_LIBRARY: ShapeDefinition[] = [
  // Basic Shapes
  {
    id: "circle",
    name: "Circle",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<circle cx="50" cy="50" r="50" fill="currentColor"/>',
  },
  {
    id: "circle-ring",
    name: "Circle Ring",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<path d="M 50 0 A 50 50 0 1 1 50 100 A 50 50 0 1 1 50 0 Z M 50 15 A 35 35 0 1 0 50 85 A 35 35 0 1 0 50 15 Z" fill="currentColor" fill-rule="evenodd"/>',
  },
  {
    id: "circle-ring-thin",
    name: "Thin Ring",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<path d="M 50 0 A 50 50 0 1 1 50 100 A 50 50 0 1 1 50 0 Z M 50 6 A 44 44 0 1 0 50 94 A 44 44 0 1 0 50 6 Z" fill="currentColor" fill-rule="evenodd"/>',
  },
  {
    id: "square",
    name: "Square",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<rect x="0" y="0" width="100" height="100" fill="currentColor"/>',
  },
  {
    id: "rounded-square",
    name: "Rounded Square",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<rect x="0" y="0" width="__W__" height="__H__" rx="15" ry="15" fill="currentColor"/>',
  },
  {
    id: "triangle",
    name: "Triangle",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<polygon points="50,0 100,100 0,100" fill="currentColor"/>',
  },
  {
    id: "diamond",
    name: "Diamond",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<polygon points="50,0 100,50 50,100 0,50" fill="currentColor"/>',
  },
  {
    id: "pentagon",
    name: "Pentagon",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<polygon points="50,0 100,36 81,95 19,95 0,36" fill="currentColor"/>',
  },
  {
    id: "hexagon",
    name: "Hexagon",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<polygon points="50,0 97,25 97,75 50,100 3,75 3,25" fill="currentColor"/>',
  },
  {
    id: "star-5",
    name: "Star",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<polygon points="50,0 62,38 100,38 69,62 81,100 50,78 19,100 31,62 0,38 38,38" fill="currentColor"/>',
  },
  {
    id: "heart",
    name: "Heart",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<path d="M50,92 C18,62 0,40 0,22 C0,6 16,0 32,12 C40,18 46,24 50,30 C54,24 60,18 68,12 C84,0 100,6 100,22 C100,40 82,62 50,92 Z" fill="currentColor"/>',
  },
  {
    id: "oval",
    name: "Oval",
    category: "Basic",
    viewBox: "0 0 100 60",
    content: '<ellipse cx="50" cy="30" rx="50" ry="30" fill="currentColor"/>',
  },
  {
    id: "bookmark",
    name: "Bookmark",
    category: "Basic",
    viewBox: "0 0 80 100",
    content: '<path d="M0,0 H80 V90 L40,75 L0,90 Z" fill="currentColor"/>',
  },
  {
    id: "magnifying-glass",
    name: "Magnifying Glass",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<circle cx="40" cy="40" r="28" fill="none" stroke="currentColor" stroke-width="8"/><line x1="60" y1="60" x2="90" y2="90" stroke="currentColor" stroke-width="10" stroke-linecap="round"/>',
  },
  {
    id: "folder",
    name: "Folder",
    category: "Basic",
    viewBox: "0 0 100 80",
    content: '<path d="M6,0 H35 Q38,0 40,5 L43,15 Q45,20 49,20 H92 Q100,20 100,28 V72 Q100,80 92,80 H8 Q0,80 0,72 V8 Q0,0 6,0 Z" fill="currentColor"/>',
  },
  {
    id: "check-mark",
    name: "Check Mark",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<polyline points="5,55 40,90 95,10" fill="none" stroke="currentColor" stroke-width="12" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: "cross-mark",
    name: "Cross Mark",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<line x1="6" y1="6" x2="94" y2="94" stroke="currentColor" stroke-width="12" stroke-linecap="round"/><line x1="94" y1="6" x2="6" y2="94" stroke="currentColor" stroke-width="12" stroke-linecap="round"/>',
  },
  {
    id: "sparkle",
    name: "Sparkle",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<path d="M50,0 Q52,45 100,50 Q52,55 50,100 Q48,55 0,50 Q48,45 50,0 Z" fill="currentColor"/>',
  },
  {
    id: "clip",
    name: "Clip",
    category: "Basic",
    viewBox: "0 0 512 512",
    content: '<path d="M80.897,495.43c-20.726,0-41.45-7.889-57.229-23.667c-31.557-31.557-31.557-82.902,0-114.458L333.82,47.15c19.719-19.719,45.937-30.579,73.825-30.579s54.105,10.86,73.825,30.579c40.707,40.707,40.707,106.943,0,147.65L258.657,417.613c-4.79,4.79-12.557,4.79-17.348,0c-4.791-4.79-4.79-12.557,0-17.348l222.813-222.813c31.142-31.142,31.142-81.814,0-112.956c-15.086-15.086-35.143-23.394-56.477-23.394c-21.334,0-41.393,8.308-56.479,23.394L41.014,374.651c-10.653,10.653-16.52,24.816-16.52,39.883s5.867,29.23,16.52,39.883c21.992,21.992,57.774,21.99,79.765,0l244.27-244.27c12.817-12.818,12.817-33.672,0-46.488c-12.815-12.817-33.671-12.817-46.487,0l-156.93,156.929c-4.789,4.79-12.557,4.79-17.346,0c-4.79-4.79-4.79-12.557,0-17.348l156.93-156.929c22.382-22.382,58.8-22.382,81.182,0c22.382,22.383,22.382,58.8,0,81.182l-244.274,244.27C122.347,487.54,101.622,495.43,80.897,495.43z" fill="currentColor"/>',
  },
  {
    id: "pill",
    name: "Pill",
    category: "Basic",
    viewBox: "0 0 100 40",
    content: '<rect x="0" y="0" width="__W__" height="__H__" rx="__R__" ry="__R__" fill="currentColor"/>',
  },
  {
    id: "rect-outline",
    name: "Rectangle Outline",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<rect x="0" y="0" width="__W__" height="__H__" fill="none" stroke="currentColor" stroke-width="3"/>',
  },
  {
    id: "rounded-rect-outline",
    name: "Rounded Rect Outline",
    category: "Basic",
    viewBox: "0 0 100 100",
    content: '<rect x="0" y="0" width="__W__" height="__H__" rx="10" ry="10" fill="none" stroke="currentColor" stroke-width="3"/>',
  },
  // Arrows
  {
    id: "arrow-right",
    name: "Arrow Right",
    category: "Arrows",
    viewBox: "0 0 100 60",
    content: '<polygon points="0,20 60,20 60,0 100,30 60,60 60,40 0,40" fill="currentColor" stroke="currentColor" stroke-width="2"/>',
  },
  {
    id: "arrow-left",
    name: "Arrow Left",
    category: "Arrows",
    viewBox: "0 0 100 60",
    content: '<polygon points="100,20 40,20 40,0 0,30 40,60 40,40 100,40" fill="currentColor" stroke="currentColor" stroke-width="2"/>',
  },
  {
    id: "arrow-up",
    name: "Arrow Up",
    category: "Arrows",
    viewBox: "0 0 60 100",
    content: '<polygon points="20,100 20,40 0,40 30,0 60,40 40,40 40,100" fill="currentColor" stroke="currentColor" stroke-width="2"/>',
  },
  {
    id: "arrow-down",
    name: "Arrow Down",
    category: "Arrows",
    viewBox: "0 0 60 100",
    content: '<polygon points="20,0 20,60 0,60 30,100 60,60 40,60 40,0" fill="currentColor" stroke="currentColor" stroke-width="2"/>',
  },
  {
    id: "chevron-right",
    name: "Chevron Right",
    category: "Arrows",
    viewBox: "0 0 60 100",
    content: '<polyline points="10,5 50,50 10,95" fill="none" stroke="currentColor" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: "chevron-left",
    name: "Chevron Left",
    category: "Arrows",
    viewBox: "0 0 60 100",
    content: '<polyline points="50,5 10,50 50,95" fill="none" stroke="currentColor" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: "chevron-up",
    name: "Chevron Up",
    category: "Arrows",
    viewBox: "0 0 100 60",
    content: '<polyline points="5,50 50,10 95,50" fill="none" stroke="currentColor" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: "chevron-down",
    name: "Chevron Down",
    category: "Arrows",
    viewBox: "0 0 100 60",
    content: '<polyline points="5,10 50,50 95,10" fill="none" stroke="currentColor" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: "thin-arrow-right",
    name: "Thin Arrow Right",
    category: "Arrows",
    viewBox: "0 0 100 60",
    content: '<line x1="5" y1="30" x2="80" y2="30" stroke="currentColor" stroke-width="6" stroke-linecap="round"/><polyline points="65,15 85,30 65,45" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: "thin-arrow-left",
    name: "Thin Arrow Left",
    category: "Arrows",
    viewBox: "0 0 100 60",
    content: '<line x1="20" y1="30" x2="95" y2="30" stroke="currentColor" stroke-width="6" stroke-linecap="round"/><polyline points="35,15 15,30 35,45" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: "thin-arrow-up",
    name: "Thin Arrow Up",
    category: "Arrows",
    viewBox: "0 0 60 100",
    content: '<line x1="30" y1="20" x2="30" y2="95" stroke="currentColor" stroke-width="6" stroke-linecap="round"/><polyline points="15,35 30,15 45,35" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: "thin-arrow-down",
    name: "Thin Arrow Down",
    category: "Arrows",
    viewBox: "0 0 60 100",
    content: '<line x1="30" y1="5" x2="30" y2="80" stroke="currentColor" stroke-width="6" stroke-linecap="round"/><polyline points="15,65 30,85 45,65" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  // Callouts & Badges
  {
    id: "speech-bubble",
    name: "Speech Bubble",
    category: "Callouts",
    viewBox: "0 0 100 80",
    content: '<path d="M8,0 H92 Q100,0 100,8 V52 Q100,60 92,60 H30 L12,80 L18,60 H8 Q0,60 0,52 V8 Q0,0 8,0 Z" fill="currentColor"/>',
  },
  {
    id: "badge",
    name: "Badge",
    category: "Callouts",
    viewBox: "0 0 100 100",
    content: '<path d="M50,0 L62,33 L100,33 L70,55 L82,95 L50,72 L18,95 L30,55 L0,33 L38,33 Z" fill="currentColor"/>',
  },
  {
    id: "banner",
    name: "Banner",
    category: "Callouts",
    viewBox: "0 0 120 40",
    content: '<path d="M0,0 H120 V30 L110,40 L100,30 H20 L10,40 L0,30 Z" fill="currentColor"/>',
  },
  {
    id: "ribbon",
    name: "Ribbon",
    category: "Callouts",
    viewBox: "0 0 100 30",
    content: '<path d="M0,15 L10,0 H90 L100,15 L90,30 H10 Z" fill="currentColor"/>',
  },
  // Lines - Basic
  {
    id: "line-thin",
    name: "Thin Line",
    category: "Lines",
    viewBox: "0 0 100 6",
    content: '<line x1="0" y1="3" x2="100" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  },
  {
    id: "line-medium",
    name: "Medium Line",
    category: "Lines",
    viewBox: "0 0 100 8",
    content: '<line x1="0" y1="4" x2="100" y2="4" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>',
  },
  {
    id: "line-thick",
    name: "Thick Line",
    category: "Lines",
    viewBox: "0 0 100 12",
    content: '<line x1="0" y1="6" x2="100" y2="6" stroke="currentColor" stroke-width="10" stroke-linecap="round"/>',
  },
  {
    id: "line-vertical",
    name: "Vertical Line",
    category: "Lines",
    viewBox: "0 0 6 100",
    content: '<line x1="3" y1="0" x2="3" y2="100" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  },
  {
    id: "line-diagonal-down",
    name: "Diagonal Down",
    category: "Lines",
    viewBox: "0 0 100 100",
    content: '<line x1="0" y1="0" x2="100" y2="100" stroke="currentColor" stroke-width="3"/>',
  },
  {
    id: "line-diagonal-up",
    name: "Diagonal Up",
    category: "Lines",
    viewBox: "0 0 100 100",
    content: '<line x1="0" y1="100" x2="100" y2="0" stroke="currentColor" stroke-width="3"/>',
  },
  // Lines - Decorative
  {
    id: "line-dashed",
    name: "Dashed Line",
    category: "Lines",
    viewBox: "0 0 100 6",
    content: '<line x1="0" y1="3" x2="100" y2="3" stroke="currentColor" stroke-width="3" stroke-dasharray="8 5" stroke-linecap="round"/>',
  },
  {
    id: "line-dotted",
    name: "Dotted Line",
    category: "Lines",
    viewBox: "0 0 100 6",
    content: '<line x1="3" y1="3" x2="97" y2="3" stroke="currentColor" stroke-width="3" stroke-dasharray="0.1 7" stroke-linecap="round"/>',
  },
  {
    id: "line-wavy",
    name: "Wavy Line",
    category: "Lines",
    viewBox: "0 0 100 16",
    content: '<path d="M0,8 Q6.25,0 12.5,8 T25,8 T37.5,8 T50,8 T62.5,8 T75,8 T87.5,8 T100,8" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>',
  },
  {
    id: "line-zigzag",
    name: "Zigzag Line",
    category: "Lines",
    viewBox: "0 0 100 16",
    content: '<polyline points="0,14 7,2 14,14 21,2 28,14 35,2 42,14 49,2 56,14 63,2 70,14 77,2 84,14 91,2 100,14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round"/>',
  },
  // Lines - Dividers
  {
    id: "divider-double",
    name: "Double Line",
    category: "Lines",
    viewBox: "0 0 100 10",
    content: '<line x1="0" y1="2" x2="100" y2="2" stroke="currentColor" stroke-width="2"/><line x1="0" y1="8" x2="100" y2="8" stroke="currentColor" stroke-width="2"/>',
  },
  {
    id: "divider-thick-thin",
    name: "Thick-Thin",
    category: "Lines",
    viewBox: "0 0 100 10",
    content: '<line x1="0" y1="3" x2="100" y2="3" stroke="currentColor" stroke-width="4"/><line x1="0" y1="9" x2="100" y2="9" stroke="currentColor" stroke-width="1.5"/>',
  },
  {
    id: "divider-diamond",
    name: "Diamond Divider",
    category: "Lines",
    viewBox: "0 0 100 12",
    content: '<line x1="0" y1="6" x2="40" y2="6" stroke="currentColor" stroke-width="2"/><polygon points="50,0 60,6 50,12 40,6" fill="currentColor"/><line x1="60" y1="6" x2="100" y2="6" stroke="currentColor" stroke-width="2"/>',
  },
  {
    id: "divider-circle",
    name: "Circle Divider",
    category: "Lines",
    viewBox: "0 0 100 12",
    content: '<line x1="0" y1="6" x2="42" y2="6" stroke="currentColor" stroke-width="2"/><circle cx="50" cy="6" r="5" fill="currentColor"/><line x1="58" y1="6" x2="100" y2="6" stroke="currentColor" stroke-width="2"/>',
  },
  {
    id: "divider-dots",
    name: "Three Dots",
    category: "Lines",
    viewBox: "0 0 100 12",
    content: '<line x1="0" y1="6" x2="34" y2="6" stroke="currentColor" stroke-width="2"/><circle cx="42" cy="6" r="3" fill="currentColor"/><circle cx="50" cy="6" r="3" fill="currentColor"/><circle cx="58" cy="6" r="3" fill="currentColor"/><line x1="66" y1="6" x2="100" y2="6" stroke="currentColor" stroke-width="2"/>',
  },
  {
    id: "divider-arrows",
    name: "Arrow Divider",
    category: "Lines",
    viewBox: "0 0 100 12",
    content: '<line x1="10" y1="6" x2="90" y2="6" stroke="currentColor" stroke-width="2"/><polyline points="0,6 10,0 10,12" fill="currentColor"/><polyline points="100,6 90,0 90,12" fill="currentColor"/>',
  },
  {
    id: "divider-tapered",
    name: "Tapered Line",
    category: "Lines",
    viewBox: "0 0 100 8",
    content: '<path d="M0,4 Q25,0 50,4 Q75,8 100,4" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>',
  },
];

// Group shapes by category
function getShapesByCategory(): Record<string, ShapeDefinition[]> {
  return SHAPE_LIBRARY.reduce(
    (acc, shape) => {
      if (!acc[shape.category]) {
        acc[shape.category] = [];
      }
      acc[shape.category].push(shape);
      return acc;
    },
    {} as Record<string, ShapeDefinition[]>
  );
}

interface ShapeLibraryViewProps {
  onBack: () => void;
  onSelect: (shape: ShapeDefinition) => void;
}

export function ShapeLibraryView({ onBack, onSelect }: ShapeLibraryViewProps) {
  const shapesByCategory = getShapesByCategory();
  const categories = Object.keys(shapesByCategory);

  const handleShapeClick = (shape: ShapeDefinition) => {
    // Immediately add the shape when clicked
    onSelect(shape);
  };

  // Render a shape preview
  const renderShapePreview = (shape: ShapeDefinition) => {
    // Resolve __W__/__H__ placeholders with viewBox dimensions for preview
    const vbParts = shape.viewBox.trim().split(/\s+/).map(Number);
    const vbW = vbParts[2] || 100;
    const vbH = vbParts[3] || 100;
    const previewContent = shape.content
      .replace(/__W__/g, String(vbW))
      .replace(/__H__/g, String(vbH))
      .replace(/__R__/g, String(Math.min(vbW, vbH) / 2));

    return (
      <div
        className="w-12 h-12 flex items-center justify-center"
        dangerouslySetInnerHTML={{
          __html: `<svg viewBox="${shape.viewBox}" class="w-full h-full" style="color: #374151;">${previewContent}</svg>`,
        }}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center mb-4">
          <button
            onClick={onBack}
            className="mr-2 p-1 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-medium">Add Shape</h3>
        </div>
      </div>

      {/* Shape Grid by Category */}
      <div className="flex-1 overflow-y-auto pr-2">
        {categories.map((category) => (
          <div key={category} className="mb-6">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">
              {category}
            </h4>
            <div className="grid grid-cols-4 gap-2">
              {shapesByCategory[category].map((shape) => (
                <div
                  key={shape.id}
                  onClick={() => handleShapeClick(shape)}
                  className="aspect-square rounded-lg border-2 border-transparent bg-muted/30 cursor-pointer transition-all flex items-center justify-center p-2 hover:bg-muted/50 hover:border-primary/50"
                  title={shape.name}
                >
                  {renderShapePreview(shape)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
